/**
 * @fileoverview Flagged Transactions — master agent chat (shared ChatSheet)
 * @module jobs/phases/review/flaggedTransactions/MasterChatSheet
 *
 * The master agent's chat uses the same premium slide-out ChatSheet as the
 * data agents (jobOverview / summary chat): messages from
 * `flaggedMasterChatMessages`, send via `sendMasterChatMessage`, thinking
 * indicator from the master task status.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import {
  ChatSheet,
  type ChatMessageData,
  type ChatUserInfo,
} from "@/ui/composed/chat";

export function MasterChatSheet(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: Id<"jobs">;
  agentBusy: boolean;
  canSend: boolean;
}) {
  const { open, onOpenChange, jobId, agentBusy, canSend } = props;
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  const { data: chatMessages } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.flaggedTransactions.publicQueries
        .listMasterChat,
      { jobId },
    ),
  );

  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const userInfo = useMemo<ChatUserInfo | undefined>(() => {
    if (!currentUser) return undefined;
    return {
      avatarUrl: currentUser.avatarUrl,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
    };
  }, [currentUser]);

  const { mutateAsync: sendMessage, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .sendMasterChatMessage,
    ),
  });

  const realMessages: ChatMessageData[] = useMemo(
    () =>
      (
        (chatMessages ?? []) as Array<{
          id: string;
          role: string;
          body: string;
          createdAt: number;
        }>
      )
        .filter((m) => m.role === "user" || m.role === "assistant")
        .map((m) => ({
          id: m.id,
          role: m.role as "user" | "assistant",
          content: m.body,
          createdAt: m.createdAt,
        })),
    [chatMessages],
  );

  const messages: ChatMessageData[] = useMemo(() => {
    if (!pendingMessage) return realMessages;
    if (
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      return realMessages;
    }
    return [
      ...realMessages,
      {
        id: "optimistic-pending",
        role: "user" as const,
        content: pendingMessage,
        createdAt: Date.now(),
      },
    ];
  }, [realMessages, pendingMessage]);

  useEffect(() => {
    if (!pendingMessage) return;
    if (
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      setPendingMessage(null);
    }
  }, [realMessages, pendingMessage]);

  const lastMessage = messages[messages.length - 1];
  const isThinking =
    agentBusy ||
    isPending ||
    (lastMessage?.role === "user" && pendingMessage !== null) ||
    (lastMessage?.role === "user" && agentBusy);

  const handleSend = async (raw: string) => {
    const message = raw.trim();
    if (!message || !canSend) return;
    setPendingMessage(message);
    try {
      await sendMessage({ jobId, message });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send");
      setPendingMessage(null);
    }
  };

  return (
    <ChatSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Recoverable Transactions"
      description="Coordinate the eight analysis agents: run or refresh them, ask about recoverable transactions, or route an instruction to a specific agent."
      messages={messages}
      onSend={(m) => void handleSend(m)}
      userInfo={userInfo}
      isSending={isPending}
      isThinking={isThinking}
      emptyMessage={
        'Ask the master agent to coordinate the review — for example "re-run all agents", "why is the preferences total so high?", or "ask the unfair loans agent to re-check the Smith facility".'
      }
      inputPlaceholder={
        canSend
          ? "Instruct or ask the master agent…"
          : "Viewers can't chat with the agent"
      }
    />
  );
}
